<template>
  <div class="modal-card modal-card-large">
    <CloseIcon :size="24" class="close-modal-icon close-on-click" />
    <div class="modal-card-header">
      <div class="header-content">
        <h1 class="title">Manage Practices</h1>
      </div>
    </div>
    <div class="modal-card-body">
      <div v-if="isLoading" class="loading-container">
        <div class="spinner"></div>
        <p class="loading-text">Loading practices...</p>
      </div>
      <div v-else-if="practices.length === 0" class="empty-state">
        <p>No practices found</p>
      </div>
      <div v-else class="practice-list">
        <div v-for="practice in practices" :key="practice.practiceId" class="practice-card">
          <div class="practice-info">
            <div class="practice-details">
              <div class="practice-name-row">
                <span class="practice-name">{{ practice.name }}</span>
              </div>
              <span v-if="practice.addressCity || practice.addressState" class="practice-location">
                {{ formatLocation(practice) }}
              </span>
            </div>
          </div>
          <div v-if="currentUser && currentUser.isAdmin" class="practice-actions">
            <button class="button icon-button" @click="handleEditPractice(practice)" title="Edit practice">
              <PencilIcon :size="18" />
            </button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="currentUser && currentUser.isAdmin" class="modal-card-footer">
      <button class="button primary form-button" @click="handleCreatePractice">
        Create Practice
      </button>
    </div>
  </div>
</template>

<script>
import CloseIcon from 'vue-material-design-icons/Close.vue';
import PencilIcon from 'vue-material-design-icons/Pencil.vue';
import * as api from '@/api';
import { queryClient } from '@/libs/queryClient';
import { alertService } from '@/libs/alert';

export default {
  name: "ManagePracticesModal",
  components: {
    CloseIcon,
    PencilIcon
  },
  emits: ['edit-practice', 'create-practice', 'close'],
  data() {
    return {
      practices: [],
      isLoading: false,
      currentUser: null
    }
  },
  created() {
    this.currentUser = queryClient.getQueryData(['user']) || null;
    this.loadPractices();
  },
  methods: {
    async loadPractices() {
      try {
        this.isLoading = true;
        this.practices = await queryClient.fetchQuery({
          queryKey: ['practices'],
          queryFn: () => api.fetchPractices()
        });
      } catch (err) {
        console.error('Failed to fetch practices:', err);
        alertService.error('Failed to load practices: ' + err.message);
        this.practices = [];
      } finally {
        this.isLoading = false;
      }
    },
    formatLocation(practice) {
      const parts = [];
      if (practice.addressCity) parts.push(practice.addressCity);
      if (practice.addressState) parts.push(practice.addressState);
      return parts.join(', ');
    },
    handleCreatePractice() {
      this.$emit('create-practice');
    },
    handleEditPractice(practice) {
      this.$emit('edit-practice', practice);
    }
  }
}
</script>

<style scoped>
.modal-card-large {
  max-width: 500px;
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  gap: 16px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #f0f0f0;
  border-top-color: var(--primary-color);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-text {
  color: #8c8c8c;
  font-size: 14px;
}

.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #8c8c8c;
  font-size: 14px;
}

.practice-list {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.practice-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  background-color: #fff;
  border-bottom: 1px solid #e5e7eb;
}

.practice-card:last-child {
  border-bottom: none;
}

.practice-info {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

.practice-details {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
}

.practice-name-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.practice-name {
  font-size: 14px;
  font-weight: 500;
  color: #262626;
}

.practice-location {
  font-size: 13px;
  color: #8c8c8c;
}

.practice-actions {
  display: flex;
  gap: 4px;
}

.icon-button {
  padding: 6px;
  min-width: auto;
  height: 31px;
  width: 31px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--border-radius);
}
</style>
